import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useCallback} from 'react';
import {Node, Transforms} from 'slate';
import {
  ReactEditor,
  type RenderElementProps,
  useReadOnly,
  useSlateStatic,
} from 'slate-react';

import {PageViews} from '../../util/datadog/artifacts/pageViews';
import {type PerformanceMetricsContext} from '../../util/datadog/artifacts/trackPageMetric';
import {useDataFetchTiming} from '../../util/datadog/artifacts/useDataFetchTiming';
import {useDebounceState} from '../../util/hooks';
import {
  useTrackCollectionOverviewGridAdded,
  useTrackCollectionOverviewVersionSearch,
} from '../ArtifactOverview/collectionOverviewAnalytics';
import {BlockWrapper} from '../Slate/plugins/drag-drop';
import {
  deriveSelectedArtifactVersionMetricAvailability,
  useArtifactVersionMetricSearch,
} from './artifactMetricData';
import {ArtifactVersionMetricAddPanelButton} from './ArtifactVersionMetricAddPanelButton';
import {
  ARTIFACT_PLACEHOLDER_LINK,
  ARTIFACT_PLACEHOLDER_SELECT,
  ArtifactVersionMetricNoMetricsEmptyState,
  ArtifactVersionMetricPlaceholderState,
} from './ArtifactVersionMetricChartPlaceholder';
import {ArtifactVersionMetricFullscreenModal} from './ArtifactVersionMetricFullscreenModal';
import {ArtifactVersionMetricPanelCard} from './ArtifactVersionMetricPanelCard';
import {useArtifactVersionMetricsContext} from './ArtifactVersionMetricsContext';
import {ArtifactVersionSelectionTable} from './ArtifactVersionSelectionTable';
import {
  type ArtifactVersionMetricGridPanelConfig,
  type ArtifactVersionsGrid,
  consumeNewlyCreatedArtifactVersionsGridId,
  isArtifactVersionsGrid,
} from './artifactVersionsGridTypes';
import {useArtifactVersionSelectionData} from './useArtifactVersionSelectionData';
import {useArtifactVersionsGridPanelController} from './useArtifactVersionsGridPanelController';
import {useEnsureEditableArtifactVersionMetricPanel} from './useEnsureEditableArtifactVersionMetricPanel';
import {useInitialArtifactVersionMembershipSelection} from './useInitialArtifactVersionMembershipSelection';
import {usePublishedArtifactVersionMembershipIds} from './usePublishedArtifactVersionMembershipIds';
import {clampArtifactVersionMembershipSelection} from './versionSelectionUtils';

export const ArtifactVersionsGridElement: React.FC<
  RenderElementProps & {
    element: ArtifactVersionsGrid;
  }
> = ({attributes, element, children}) => {
  const editor = useSlateStatic();
  const readOnly = useReadOnly();
  const {
    artifactCollectionId,
    analyticsContext,
    closePanelEditor,
    panelEditor,
    syncPanelEditor,
    togglePanelEditor,
  } = useArtifactVersionMetricsContext();
  const {metadata} = element;
  const resolvedGridId = metadata.gridId ?? '';
  // oxlint-disable-next-line react/refs
  const isNewlyCreatedGrid = React.useRef(
    consumeNewlyCreatedArtifactVersionsGridId(resolvedGridId)
  ).current;
  const [localPublishedMembershipIds, setLocalPublishedMembershipIds] =
    usePublishedArtifactVersionMembershipIds(metadata.membershipIds);
  // Older saved grids may contain more selected versions than the current
  // plotting limit. Clamp before querying/rendering so they don't fan out into
  // oversized metric-value requests.
  const displayedMembershipIds = clampArtifactVersionMembershipSelection(
    readOnly ? localPublishedMembershipIds : metadata.membershipIds
  );
  const [search, debouncedSearch, setSearch] = useDebounceState('', 300);
  const searchPending = search.trim() !== debouncedSearch.trim();
  const versionSelection = useArtifactVersionSelectionData({
    artifactCollectionId,
    searchTerm: debouncedSearch,
  });
  const metricsContext: PerformanceMetricsContext = {
    pageName: PageViews.ArtifactCollectionVersionMetrics,
    artifactCollectionId,
    artifactCollectionName: analyticsContext?.artifactCollectionName,
    artifactType: analyticsContext?.artifactTypeName,
    collectionType: analyticsContext?.collectionType,
    entityName: analyticsContext?.entityName,
    projectName: analyticsContext?.projectName,
    source: analyticsContext?.source,
    hasSearchTerm: debouncedSearch.trim() !== '',
    searchLength: debouncedSearch.trim().length,
  };
  useDataFetchTiming(
    metricsContext,
    versionSelection.loading,
    'artifact_version_metrics_versions',
    versionSelection.rowCount
  );
  useTrackCollectionOverviewVersionSearch({
    analyticsContext,
    gridId: resolvedGridId,
    searchTerm: debouncedSearch,
    selectedVersionCount: displayedMembershipIds.length,
  });
  const searchStatus =
    searchPending || (versionSelection.loading && search.trim() !== '')
      ? 'Searching...'
      : undefined;
  const hasActiveSearch = search.trim() !== '' || debouncedSearch.trim() !== '';
  const hasNoVersions =
    !versionSelection.loading &&
    versionSelection.rowCount === 0 &&
    !hasActiveSearch;

  const updateMetadata = useCallback(
    (
      getNextMetadata: (
        metadata: ArtifactVersionsGrid['metadata']
      ) => ArtifactVersionsGrid['metadata']
    ) => {
      const path = ReactEditor.findPath(editor, element);
      const currentNode = Node.get(editor, path);

      if (!isArtifactVersionsGrid(currentNode)) {
        return;
      }

      Transforms.setNodes(
        editor,
        {metadata: getNextMetadata(currentNode.metadata)},
        {at: path}
      );
    },
    [editor, element]
  );

  useTrackCollectionOverviewGridAdded({
    analyticsContext,
    gridId: resolvedGridId,
    isNewlyCreatedGrid,
    panelCount: metadata.panels.length,
    selectedVersionCount: displayedMembershipIds.length,
  });

  const updatePanelConfig = useCallback(
    (
      panelId: string,
      config: Partial<ArtifactVersionMetricGridPanelConfig>
    ) => {
      updateMetadata(metadata => ({
        ...metadata,
        panels: metadata.panels.map(panel =>
          panel.__id__ === panelId
            ? {...panel, config: {...panel.config, ...config}}
            : panel
        ),
      }));
    },
    [updateMetadata]
  );

  useEnsureEditableArtifactVersionMetricPanel({
    panelCount: metadata.panels.length,
    readOnly,
    updateMetadata,
  });

  const updateMembershipIds = useCallback(
    (membershipIds: string[]) => {
      const clampedMembershipIds =
        clampArtifactVersionMembershipSelection(membershipIds);

      if (readOnly) {
        setLocalPublishedMembershipIds(clampedMembershipIds);
        return;
      }

      updateMetadata(metadata => ({
        ...metadata,
        membershipIds: clampedMembershipIds,
      }));
    },
    [readOnly, setLocalPublishedMembershipIds, updateMetadata]
  );

  const {waitingForInitialSelection} =
    useInitialArtifactVersionMembershipSelection({
      artifactCollectionId,
      debouncedSearch,
      loading: versionSelection.loading,
      membershipIds: metadata.membershipIds,
      page: versionSelection.page,
      pageRows: versionSelection.pageRows,
      readOnly,
      search,
      updateMembershipIds,
    });

  const {
    addPanel,
    canDeletePanel,
    canOpenFullscreenPanel,
    configuredMetricNames,
    closeFullscreenPanel,
    deletePanel,
    fullscreenPanel,
    fullscreenPanelIndex,
    fullscreenPanels,
    hasConfiguredPanels,
    openFullscreenPanel,
    openPanelEditor,
    ownsPanelEditorSession,
    panelsToRender,
    selectPanelChartType,
    showNextFullscreenPanel,
    showPreviousFullscreenPanel,
  } = useArtifactVersionsGridPanelController({
    analyticsContext,
    closePanelEditor,
    displayedMembershipIds,
    gridId: resolvedGridId,
    metadata,
    panelEditor,
    readOnly,
    syncPanelEditor,
    togglePanelEditor,
    updateMetadata,
    updatePanelConfig,
  });
  const selectedMetrics = useArtifactVersionMetricSearch({
    artifactCollectionId,
    analyticsContext,
    enabled: !readOnly && !hasConfiguredPanels,
    first: 1,
    membershipIds: displayedMembershipIds,
  });
  const selectedMetricAvailability =
    deriveSelectedArtifactVersionMetricAvailability({
      enabled: !readOnly && !hasConfiguredPanels,
      loading: selectedMetrics.loading,
      membershipIds: displayedMembershipIds,
      metricNames: selectedMetrics.metricNames,
    });
  const hasNoNumericMetrics = selectedMetricAvailability.hasNoMetricsAvailable;
  const hasNoSelectedVersions =
    !readOnly && !hasConfiguredPanels && displayedMembershipIds.length === 0;
  // Until the first load + numeric-metric eligibility resolves we can't tell
  // whether to show the unconfigured panel placeholder or the "no plottable
  // metrics" empty state, so show a loader rather than flashing the wrong one.
  const isResolvingInitialState =
    !readOnly &&
    !hasConfiguredPanels &&
    !hasActiveSearch &&
    (versionSelection.loading ||
      selectedMetricAvailability.loading ||
      waitingForInitialSelection);

  if (readOnly && panelsToRender.length === 0) {
    return (
      <BlockWrapper
        attributes={attributes}
        element={element}
        disableClickSelect>
        {children}
      </BlockWrapper>
    );
  }

  return (
    <BlockWrapper attributes={attributes} element={element} disableClickSelect>
      <TailwindContents>
        <div
          data-artifact-versions-grid
          className="my-16 w-full max-w-full md:max-w-[900px] min-w-0 rounded-lg border border-solid border-wb-border bg-wb-surface-sunken p-16"
          data-test="artifact-version-metrics-grid">
          {isResolvingInitialState ? (
            <div className="flex min-h-[260px] w-full items-center justify-center">
              <WaveLoader size="small" />
            </div>
          ) : hasNoVersions ? (
            <ArtifactVersionMetricPlaceholderState
              message={ARTIFACT_PLACEHOLDER_LINK}
            />
          ) : hasNoSelectedVersions ? (
            <ArtifactVersionMetricPlaceholderState
              message={ARTIFACT_PLACEHOLDER_SELECT}
            />
          ) : hasNoNumericMetrics ? (
            <ArtifactVersionMetricNoMetricsEmptyState />
          ) : (
            <div
              className="flex w-full min-w-0 flex-wrap gap-12"
              data-test="artifact-version-metrics-panel-list">
              {panelsToRender.map(panel => (
                <div key={panel.__id__} className="min-w-0 flex-[1_1_360px]">
                  <ArtifactVersionMetricPanelCard
                    artifactCollectionId={artifactCollectionId}
                    canDeletePanel={canDeletePanel}
                    deletePanel={deletePanel}
                    isEditing={
                      ownsPanelEditorSession &&
                      panelEditor?.panel.__id__ === panel.__id__
                    }
                    membershipIds={displayedMembershipIds}
                    panel={panel}
                    readOnly={readOnly}
                    onEditPanel={openPanelEditor}
                    onOpenFullscreen={
                      canOpenFullscreenPanel(panel)
                        ? openFullscreenPanel
                        : undefined
                    }
                    onSelectChartType={selectPanelChartType}
                  />
                </div>
              ))}
            </div>
          )}
          {!readOnly &&
            hasConfiguredPanels &&
            !hasNoVersions &&
            !hasNoNumericMetrics && (
              <div className="mt-12 flex justify-center">
                <ArtifactVersionMetricAddPanelButton onAddPanel={addPanel} />
              </div>
            )}
          <div className="mt-12">
            <ArtifactVersionSelectionTable
              artifactCollectionId={artifactCollectionId}
              emptyRowMessage={hasNoVersions ? 'No versions yet' : undefined}
              hasConfiguredPanels={hasConfiguredPanels}
              loading={versionSelection.loading}
              membershipIds={displayedMembershipIds}
              metricNames={configuredMetricNames}
              page={versionSelection.page}
              readOnly={readOnly}
              rowCount={versionSelection.rowCount}
              rows={versionSelection.pageRows}
              search={search}
              searchStatus={searchStatus}
              setPage={versionSelection.setPage}
              setSearch={setSearch}
              updateMembershipIds={updateMembershipIds}
            />
          </div>
        </div>
      </TailwindContents>
      {fullscreenPanel != null && (
        <ArtifactVersionMetricFullscreenModal
          artifactCollectionId={artifactCollectionId}
          currentIndex={fullscreenPanelIndex}
          membershipIds={displayedMembershipIds}
          panel={fullscreenPanel}
          panelCount={fullscreenPanels.length}
          onClose={closeFullscreenPanel}
          onNext={showNextFullscreenPanel}
          onPrevious={showPreviousFullscreenPanel}
        />
      )}
      {children}
    </BlockWrapper>
  );
};
